<script setup>
/**
 * "then → …" — the one control every branch on every card is made of.
 *
 * THERE IS EXACTLY ONE OF THESE, and that is the point. A task has five kinds of branch
 * (a step's own "then", an option's "then", "if nothing came back", "if it worked", "if it
 * failed") and they are all the same question: which step runs next. Five bespoke pickers
 * would be five places for "finish the task here" to be spelled differently, and for a
 * deleted step to leave behind a dangling link that renders as a blank box.
 *
 * A LINK TO A STEP THAT NO LONGER EXISTS IS SHOWN, NOT SWALLOWED. `WasloSelect` renders
 * only the options it is given, so a value not among them paints an empty control — the
 * owner deletes a step and every branch that pointed at it silently reads "finish here",
 * which is a different task. So a missing target is added to the list as a disabled option
 * that says what it is; the card's problem line says the same thing in words.
 */
import { computed } from 'vue';

import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** A step id, or `''` for "the task finishes here". */
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  /** `[{ id, name, type }]` — every step on the task, in card order. */
  steps: { type: Array, default: () => [] },
  /** The step this picker sits on, which cannot be its own next. */
  exclude: { type: String, default: '' },
  /** What `''` means here. Empty uses "Finish the task here". */
  emptyLabel: { type: String, default: '' },
  /** When false, `''` is not offered at all — `ifNone` has to go somewhere. */
  allowEmpty: { type: Boolean, default: true },
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.EDITOR.FLOW.LINK.${name}`;

/** "3 · Pick a time" — the number is how the card is labelled, so they match on screen. */
const optionLabel = (step, index) =>
  t(key('OPTION'), {
    number: index + 1,
    name: step.name?.trim() || t(key('UNNAMED')),
  });

const choices = computed(() => {
  const list = [];
  if (props.allowEmpty) {
    list.push({
      value: '',
      label: props.emptyLabel || t(key('END')),
    });
  }
  props.steps.forEach((step, index) => {
    if (step.id === props.exclude) return;
    list.push({ value: step.id, label: optionLabel(step, index) });
  });
  // The dangling target, named. Disabled, so it can be read but never chosen again.
  const known = props.steps.some(step => step.id === props.modelValue);
  if (props.modelValue && !known) {
    list.push({
      value: props.modelValue,
      label: t(key('MISSING'), { id: props.modelValue }),
      disabled: true,
    });
  }
  return list;
});
</script>

<template>
  <WasloSelect
    :model-value="modelValue"
    :label="label"
    :options="choices"
    :message="message"
    :message-type="message ? 'error' : 'info'"
    data-waslo-step-link
    @update:model-value="value => emit('update:modelValue', value)"
  />
</template>
